import { useEffect, useState } from 'react';
import omit from 'lodash/omit';

import { Alert, Button } from '@/shared/ui';
import { useAppDispatch } from '@/shared/hooks/store';
import { IProvider } from '@/shared/types/interfaces/provider';
import { IPharmacy } from '@/shared/types/interfaces/pharmacy';
import { IMedication } from '@/shared/types/interfaces/medication';
import { CustomersController } from '@/shared/endpoints/customers';
import { ITransferPrescriptions } from '@/shared/types/interfaces/transfer-prescriptions';

import { Provider } from './provider';
import { Pharmacy } from './pharmacy';
import { withScrollRestoration } from '@/shared/hocs/withScrollRestoration';
import { toast } from 'react-toastify';

interface IProps {
    medication: IMedication;
    onCreateMedication?: (result?: ITransferPrescriptions | null) => void;
    transferPrescription?: ITransferPrescriptions;
    zipCode: string;
    onLoadingChange?: (loading: boolean) => void;
}

const PrescriptionMedicationForm = ({ medication, onCreateMedication, transferPrescription, zipCode, onLoadingChange }: IProps) => {
    const dispatch = useAppDispatch();
    const [selectedPharmacy, setSelectedPharmacy] = useState<IPharmacy | null>(transferPrescription?.fromPharmacy || null);
    const [selectedProvider, setSelectedProvider] = useState<IProvider | null>(transferPrescription?.provider || null);
    const [isFormValid, setIsFormValid] = useState(true);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const onAddMedication = async () => {
        if (!selectedPharmacy || !selectedProvider) {
            setIsFormSubmitted(true);
            return setIsFormValid(false);
        }

        try {
            setLoading(true);

            if (transferPrescription) {
                await dispatch(CustomersController.removeTransferPrescription(transferPrescription));
            }

            const data = {
                prescribable: medication,
                provider: omit(selectedProvider, ['display']),
                fromPharmacy: omit(selectedPharmacy, ['display']),
                toPharmacyNpi: import.meta.env.REACT_APP_PHARMACY_NPI || ''
            } as ITransferPrescriptions;

            const result = await dispatch(CustomersController.addTransferPrescription(data));

            await onCreateMedication?.(
                result.meta.requestStatus === 'fulfilled'
                    ? (result.payload as ITransferPrescriptions)
                    : null
            );
        } catch (error) {
            toast.error('Failed to add medication');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isFormSubmitted) setIsFormValid(!!selectedPharmacy && !!selectedProvider);
    }, [isFormSubmitted, selectedPharmacy, selectedProvider]);

    useEffect(() => {
        onLoadingChange?.(loading);
    }, [loading]);

    return (
        <div className="prescription-medication-form" data-testid="prescription-medication-form">
            <Provider
                isEditMode={!!transferPrescription}
                error={!selectedProvider && !isFormValid}
                selectedProvider={selectedProvider}
                setSelectedProvider={setSelectedProvider}
                zipCode={zipCode}
                disabled={loading}
            />
            <Pharmacy
                error={!selectedPharmacy && !isFormValid}
                isEditMode={!!transferPrescription}
                selectedPharmacy={selectedPharmacy}
                zipCode={zipCode}
                setSelectedPharmacy={setSelectedPharmacy}
                disabled={loading}
            />
            {!isFormValid && <Alert boldLabel={false} testId="prescription-medication-form-error-message" label="Please complete all fields to proceed." className="mt-3" />}
            <Button
                variant="contained"
                disabled={!isFormValid || loading}
                className="prescriptions__form_save_btn mt-[25px] w-full"
                onClick={onAddMedication}
                testId="button-medication-prescrition-save"
                showEndIcon
                fontClassName='font-bold'
            >
                Save & Add
            </Button>
        </div>
    );
};

export default withScrollRestoration(PrescriptionMedicationForm);
